import React, { useCallback, useEffect, useMemo, useState } from "react";

import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import colors from "app/colors";
import { FileGalleryFragment } from "gql/graphql";
import { ArrowLeftIcon, ArrowRightIcon } from "modules/icons";
import Lightbox, {
  useController,
  useLoseFocus,
  useNavigationState,
  ViewCallbackProps,
} from "yet-another-react-lightbox";
import Inline from "yet-another-react-lightbox/plugins/inline";
import "yet-another-react-lightbox/styles.css";
import SliderFile from "./slider-file";
import { isImage } from "modules/utils/Functions";
import { FILE_GALLERY_NAV_Z_INDEX } from "modules/providers/chakra-provider/chakra-portal-stack";

// Register a custom YARL slide type carrying our file record. Every slide is a
// "file-slide" rendered by SliderFile (image, PDF/Office iframe, video/audio,
// text, or an unsupported-type fallback) — this gallery is a general file
// viewer, not an image-only lightbox.
declare module "yet-another-react-lightbox" {
  interface SlideTypes {
    "file-slide": {
      type: "file-slide";
      file: FileGalleryFragment;
    };
  }
}

interface IProps {
  files: FileGalleryFragment[];
  initialFile: FileGalleryFragment;
  allowDelete: boolean;
}

const FileGallery = ({ files, initialFile, allowDelete }: IProps) => {
  const initialIndex =
    files.findIndex((file) => file.id === initialFile?.id) || 0;
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  const slides = useMemo(
    () => files.map((file) => ({ type: "file-slide" as const, file })),
    [files],
  );

  const preloadAdjacentSlides = useCallback(
    async (currentIndex: number) => {
      const { preloadImage } = await import("../services/image/image-service");

      const indices = [
        currentIndex - 2,
        currentIndex - 1,
        currentIndex + 1,
        currentIndex + 2,
      ];

      indices.forEach((i) => {
        if (i < 0 || i >= files.length) return;
        const file = files[i];
        if (!isImage(file.contentType) || !file.cloudFrontUrl) return;
        preloadImage(file.cloudFrontUrl);
      });
    },
    [files],
  );

  const onView = useCallback(
    ({ index }: ViewCallbackProps) => {
      setActiveIndex(index);
      preloadAdjacentSlides(index);
    },
    [preloadAdjacentSlides],
  );

  // Inline mode has no portal-open lifecycle, so preload the initial neighbors
  // on mount (replaces react-slick's onInit).
  useEffect(() => {
    preloadAdjacentSlides(initialIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Lightbox
      plugins={[Inline]}
      index={initialIndex}
      slides={slides}
      // finite: no wrap-around (was infinite={false}). padding/spacing 0: our
      // slides are full-bleed file panels, not centered thumbnails, so the
      // header/toolbar must reach the slide edges (YARL defaults to 16px pad /
      // 30% spacing).
      carousel={{ finite: true, padding: 0, spacing: 0 }}
      // disableSwipeNavigation: arrows/keyboard only, so swipe/drag never
      // steals pointer events from iframe and video slides (was
      // swipe/touchMove={false}).
      controller={{ disableSwipeNavigation: true }}
      on={{ view: onView }}
      render={{
        slide: ({ slide }) =>
          slide.type === "file-slide" ? (
            <SliderFile
              file={slide.file}
              allowDelete={allowDelete}
              isActive={slide.file.id === files[activeIndex]?.id}
            />
          ) : null,
        controls: () => <CarouselScrollGuard />,
        buttonPrev: () => <PreviousButton />,
        buttonNext: () => <NextButton />,
      }}
    />
  );
};

export default FileGallery;

// Focusing a control inside a slide (e.g. the image zoom buttons, or the toolbar
// when a disabled arrow hands focus off) makes the browser scroll YARL's
// overflow-hidden container to reveal it. That shoves the flex-centered carousel
// sideways and leaves a blank placeholder slide in view. YARL never scrolls this
// container itself, so pin it back to the origin whenever it drifts.
const CarouselScrollGuard = () => {
  const { containerRef } = useController();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const reset = () => {
      if (el.scrollLeft !== 0) el.scrollLeft = 0;
      if (el.scrollTop !== 0) el.scrollTop = 0;
    };

    el.addEventListener("scroll", reset);
    return () => el.removeEventListener("scroll", reset);
  }, [containerRef]);

  return null;
};

const PreviousButton = () => {
  const { prev, focus } = useController();
  const { prevDisabled } = useNavigationState();
  // Hand focus back to the in-view container when this arrow becomes disabled at
  // the first slide, so focus can't escape to an off-center slide control (which
  // would scroll the carousel — see CarouselScrollGuard). Mirrors YARL's own
  // NavigationButton.
  const loseFocus = useLoseFocus(focus, prevDisabled);

  return (
    <CircularIconButton
      position="absolute"
      top="50%"
      left={0}
      zIndex={FILE_GALLERY_NAV_Z_INDEX}
      icon={<ArrowLeftIcon fill={colors.blackSecondary} boxSize={8} />}
      onClick={() => prev()}
      aria-label="previous"
      bg={colors.gray40}
      disabled={prevDisabled}
      variant="hover"
      {...loseFocus}
    />
  );
};

const NextButton = () => {
  const { next, focus } = useController();
  const { nextDisabled } = useNavigationState();
  const loseFocus = useLoseFocus(focus, nextDisabled);

  return (
    <CircularIconButton
      position="absolute"
      top="50%"
      right={0}
      zIndex={FILE_GALLERY_NAV_Z_INDEX}
      icon={<ArrowRightIcon fill={colors.blackSecondary} boxSize={8} />}
      onClick={() => next()}
      aria-label="next"
      bg={colors.gray40}
      disabled={nextDisabled}
      variant="hover"
      {...loseFocus}
    />
  );
};
